import { useEffect, useState } from 'react';

import i18n from 'i18n-react';
import { Redirect } from 'react-router-dom';

import type { CollectionItem, ImageCollectionItem, VideoCollectionItem } from 'components/Collection/Collection';
import { Collection } from 'components/Collection/Collection';
import SweetAlert from 'components/SweetAlert';

import { useAppInfo } from 'hooks/useAppInfo/useAppInfo';
import { useCollectionData } from 'hooks/useCollectionData';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { useSettings } from 'hooks/useSettings';

interface MatchParams {
  videoId?: string;
}

export interface CollectionViewProps {
  match: { url: string; params: MatchParams };
}

export function CollectionView({ match }: CollectionViewProps) {
  const { url, params } = match;
  const { videoId } = params;
  const { salesCompanionAppId } = useSettings();
  const disabledImageShare = useFeatureFlag('disabled_image_share');
  const showImageLicenseNotice = useFeatureFlag('show_image_license_notice');
  const { data: appInfoData } = useAppInfo();
  const clientName = appInfoData?.clientName ?? '';

  const [escaped, setEscaped] = useState(false);

  const lastSegment = url.split('/').slice(-1).pop() ?? '';

  const [showImageNotice, setShowImageNotice] = useState(() => {
    return showImageLicenseNotice && lastSegment === 'images' && !sessionStorage.getItem('imageGalleryNoticeShown');
  });

  const { data: collection = [] } = useCollectionData(lastSegment, salesCompanionAppId);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setEscaped(true);
    };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('keydown', onEsc);
    };
  }, []);

  const dismissImageNotice = (): void => {
    sessionStorage.setItem('imageGalleryNoticeShown', 'true');
    setShowImageNotice(false);
  };

  const redirPath =
    videoId !== undefined ? url.substring(0, url.lastIndexOf('/')).lastIndexOf('/') : url.lastIndexOf('/');

  return (
    <div className="collection-wrapper">
      <div className="collection-wrapper__overlay" />
      <SweetAlert
        trigger={showImageNotice}
        title={i18n.translate('imageGallery.noticeTitle')}
        text={i18n.translate('imageGallery.noticeText', { clientName })}
        confirmButtonText={i18n.translate('general.close')}
        onClose={dismissImageNotice}
      />

      {lastSegment === 'images' &&
        collection.map(({ links }, index) => {
          const imageItems = links as ImageCollectionItem[];
          const firstItem = imageItems[0];
          return (
            <Collection
              key={firstItem?.chapter.chapterNumber ?? index}
              header={firstItem?.chapter.chapterTitle}
              subheader={
                firstItem && (
                  <>
                    {i18n.translate('general.chapter')} {firstItem.chapter.chapterNumber}
                  </>
                )
              }
              items={imageItems}
              disabledImageShare={disabledImageShare}
            />
          );
        })}

      {lastSegment === 'videos' &&
        collection.map(({ links }, index) => {
          const videoItems = links as VideoCollectionItem[];
          const firstItem = videoItems[0];
          return <Collection key={index} header={firstItem?.chapter.chapterTitle} items={videoItems} />;
        })}

      {lastSegment === 'image-library' &&
        collection.map(({ links }, index) => {
          return (
            <div className="brochures" key={(links as CollectionItem[])[0]?.pageID ?? index}>
              <Collection
                header="Image Library"
                items={links as CollectionItem[]}
                disabledImageShare={disabledImageShare}
              />
            </div>
          );
        })}

      {lastSegment !== 'images' &&
        lastSegment !== 'image-library' &&
        lastSegment !== 'videos' &&
        collection.map(({ title, links }) => {
          return (
            <div className={`brochures ${lastSegment}`} key={(links as CollectionItem[])[0]?.pageID ?? title}>
              <Collection header={title} items={links as CollectionItem[]} disabledImageShare={disabledImageShare} />
            </div>
          );
        })}

      {escaped && <Redirect to={url.substring(0, redirPath)} />}
    </div>
  );
}
